import { useStore } from '@nanostores/react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { InstallView } from '@dunea/shared';
import { chatId } from '~/lib/persistence/useChatHistory';
import { apiListInstalls } from '~/lib/api/marketplace';
import { shouldResumeRemix } from '~/lib/storefront-links';
import { $marketplaceIntent } from '~/lib/stores/marketplaceIntent';
import { pendingRemixSlug, resumePaidRemix } from './install-actions';
import { Segmented } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { BrowseView } from './BrowseView';
import { ExpertDrawer, ExpertsView } from './ExpertsView';
import { InstalledView } from './InstalledView';
import { ListingDrawer } from './ListingDrawer';
import {
  installFor,
  MARKETPLACE_VIEWS,
  marketplaceReturn,
  withoutMarketplaceParams,
  type MarketplaceView,
} from './marketplace-model';
import { PluginPanelFrame } from './PluginPanelFrame';
import { ThemesView } from './ThemesView';
import { WishlistView } from './WishlistView';

/**
 * Owner-facing Marketplace: browse and install plugins, themes and skills,
 * start from templates, hire experts, vote on the wishlist, and manage
 * what's installed on this app (the app id IS the chat id). Paid installs leave
 * for Stripe Checkout and come back with `?marketplace=success&install=<id>`.
 */
export function MarketplacePanel() {
  const appId = useStore(chatId);

  return (
    <div className="relative flex h-full flex-col">
      {appId ? (
        <MarketplaceBody key={appId} appId={appId} />
      ) : (
        <>
          <PanelHeader />
          <div className="flex flex-1 items-center justify-center px-6 text-center text-[12px] text-muted">
            Start building to add plugins, themes and skills.
          </div>
        </>
      )}
    </div>
  );
}

function PanelHeader({ tabs }: { tabs?: React.ReactNode }) {
  return (
    <div className="space-y-3 border-b border-hairline px-4 py-3.5">
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 ring-1 ring-inset ring-accent/15">
            <div className="i-ph:squares-four text-[13px] text-accent" />
          </div>
          <h3 className="text-[13px] font-medium tracking-tight text-primary">Marketplace</h3>
        </div>
        <Link
          to="/developers"
          className="inline-flex items-center gap-1 text-[11px] text-muted transition-colors hover:text-primary"
        >
          Build for Dunea
          <div className="i-ph:arrow-up-right text-[11px]" />
        </Link>
      </div>
      {tabs}
    </div>
  );
}

function MarketplaceBody({ appId }: { appId: string }) {
  const navigate = useNavigate();
  const intent = useStore($marketplaceIntent);
  const [view, setView] = useState<MarketplaceView>(() =>
    typeof window !== 'undefined' && marketplaceReturn(window.location.search) ? 'installed' : 'plugins',
  );
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [openExpert, setOpenExpert] = useState<string | null>(null);
  const [focusWish, setFocusWish] = useState<string | null>(null);
  /** The listing a storefront deep link opened (its install is attributed to the storefront). */
  const [storefrontSlug, setStorefrontSlug] = useState<string | null>(null);
  const [panelInstall, setPanelInstall] = useState<InstallView | null>(null);
  const installs = useResource(() => apiListInstalls(appId), appId);

  // Back from Stripe Checkout: say how it went once, refresh, then drop the marker.
  useEffect(() => {
    const outcome = marketplaceReturn(window.location.search);

    if (!outcome) {
      return;
    }

    window.history.replaceState(window.history.state, '', withoutMarketplaceParams(window.location.href));

    if (outcome.outcome !== 'success') {
      toast.info('Checkout was cancelled. Nothing was charged.');
      void installs.reload();

      return;
    }

    void (async () => {
      const list = await apiListInstalls(appId).catch(() => null);

      if (list) {
        installs.setData(list);
      }

      if (shouldResumeRemix({ pendingSlug: pendingRemixSlug(), installId: outcome.installId, installs: list })) {
        toast.info('Payment received. Setting up your new site.');
        await resumePaidRemix(navigate);
      } else {
        toast.success('Payment received. Your install is activating.');
      }
    })();
  }, []);

  // Storefront deep links (?marketplace=, ?expert=, ?wishlist=, ?wish=) land here.
  useEffect(() => {
    if (!intent) {
      return;
    }

    $marketplaceIntent.set(null);
    setView(intent.view);
    setOpenExpert(intent.expert);
    setFocusWish(intent.wishId);
    setOpenSlug(intent.slug);
    setStorefrontSlug(intent.slug);
  }, [intent]);

  const upsertInstall = useCallback(
    (next: InstallView) => installs.setData((prev) => [next, ...(prev ?? []).filter((i) => i.id !== next.id)]),
    [installs.setData],
  );

  const closeDrawer = useCallback(() => setOpenSlug(null), []);
  const closeExpert = useCallback(() => setOpenExpert(null), []);

  return (
    <>
      <PanelHeader
        tabs={
          <Segmented
            value={view}
            options={MARKETPLACE_VIEWS}
            onChange={(v) => {
              setView(v);

              if (v === 'installed') {
                void installs.reload();
              }
            }}
            label="Marketplace sections"
          />
        }
      />

      <div className="flex-1 overflow-y-auto p-4">
        {view === 'plugins' && <BrowseView key="plugins" tab="plugins" installs={installs.data} onOpen={setOpenSlug} />}
        {view === 'templates' && (
          <BrowseView key="templates" tab="templates" installs={installs.data} onOpen={setOpenSlug} />
        )}
        {view === 'skills' && <BrowseView key="skills" tab="skills" installs={installs.data} onOpen={setOpenSlug} />}
        {view === 'experts' && <ExpertsView onOpen={setOpenExpert} />}
        {view === 'wishlist' && <WishlistView onOpenListing={setOpenSlug} focusWishId={focusWish} />}
        {view === 'themes' && (
          <ThemesView
            appId={appId}
            installs={installs.data}
            onInstalled={() => void installs.reload()}
            onOpen={setOpenSlug}
          />
        )}
        {view === 'installed' && (
          <InstalledView
            appId={appId}
            installs={installs}
            onChanged={(next) => installs.setData(next)}
            onOpenListing={setOpenSlug}
            onOpenPanel={setPanelInstall}
            onBrowse={() => setView('plugins')}
          />
        )}
      </div>

      {openExpert && (
        <ExpertDrawer appId={appId} handle={openExpert} onClose={closeExpert} onOpenListing={setOpenSlug} />
      )}

      {openSlug && (
        <ListingDrawer
          appId={appId}
          slug={openSlug}
          install={installFor(installs.data, openSlug)}
          source={openSlug === storefrontSlug ? 'storefront' : 'workbench'}
          onClose={closeDrawer}
          onInstalled={upsertInstall}
          onManage={() => {
            setOpenSlug(null);
            setView('installed');
          }}
        />
      )}

      {panelInstall && <PluginPanelFrame appId={appId} install={panelInstall} onClose={() => setPanelInstall(null)} />}
    </>
  );
}
